import {
  faCheck,
  faPencil,
  faTrashCan,
} from '@fortawesome/pro-light-svg-icons';
import { IconLinkButton } from '@foxtrotplatform/developer-platform-common-ui-component-lib';
import {
  TeamRole,
  useOrganization,
  useTeam,
} from '@foxtrotplatform/platform-micro-site-core';
import { TableCell, TableRow } from '@windmill/react-ui';
import React, { FunctionComponent } from 'react';
import { useForm } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { removeFromTeam, updateMemberRole } from '../../common/api/team';
import { FriendlyUserRole } from '../../common/enums';
import { addToast } from '../../common/reducers/toasterSlice';
import { RoleTypeComponent } from './HelperComponents';

interface TeamMemberProps {
  member: TeamMember;
  toggleEdit: (id: string) => void;
}

interface TeamMemberEditProps extends TeamMemberProps {
  removeFromTable: (id: string) => void;
  updateTable: (id: string, role: TeamRole) => void;
}

interface TeamMemberTableProps {
  email: React.ReactNode;
  role: React.ReactNode;
  action: React.ReactNode;
  fullName?: React.ReactNode;
}

const TeamMemberTable: FunctionComponent<TeamMemberTableProps> = ({
  email,
  role,
  action,
  fullName,
}): JSX.Element => {
  return (
    <TableRow className="h-10">
      <TableCell className="h-10 w-3/5">
        {email}
        {fullName}
      </TableCell>
      <TableCell className="w-1/3">{role}</TableCell>
      <TableCell>{action}</TableCell>
    </TableRow>
  );
};

export const TeamMemberComponent: FunctionComponent<TeamMemberProps> = ({
  member,
  toggleEdit,
}) => {
  const toggle = () => {
    toggleEdit(member.id);
  };
  return (
    <TeamMemberTable
      action={
        <div className="flex flex-row justify-center">
          <IconLinkButton icon={faPencil} linkText="Edit" onClick={toggle} />
        </div>
      }
      email={<span>{member.email}</span>}
      role={<RoleTypeComponent role={member.role} />}
    />
  );
};

interface EditUserForm {
  role: TeamRole;
}

export const TeamMemberEditComponent: FunctionComponent<
  TeamMemberEditProps
> = ({ member, updateTable, toggleEdit, removeFromTable }) => {
  const { currentOrg } = useOrganization();
  const { currentTeam } = useTeam();
  const dispatch = useDispatch();
  const { register, handleSubmit } = useForm<EditUserForm>();

  const onSubmit = async (form: EditUserForm) => {
    try {
      await updateMemberRole(
        currentOrg?.id as string,
        currentTeam?.id as string,
        member.id,
        TeamRole[form.role]
      );
      const toast = {
        message: `successfully updated ${
          member.fullName ? member.fullName : member.email
        }'s role to ${FriendlyUserRole[form.role]}`,
        isError: false,
      } as ToasterState;
      dispatch(addToast(toast));
      updateTable(member.id, form.role);
      toggleEdit('');
    } catch {
      const toast = {
        message: `there was an error updating ${
          member.fullName ? member.fullName : member.email
        }'s role to ${FriendlyUserRole[form.role]}`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(toast));
      toggleEdit('');
    }
  };

  const removeMember = async (id: string) => {
    try {
      await removeFromTeam(
        currentOrg?.id as string,
        currentTeam?.id as string,
        id
      );
      removeFromTable(id);
    } catch {
      const toast = {
        message: `there was an error removing ${
          member.fullName ? member.fullName : member.email
        } from team`,
        isError: true,
      } as ToasterState;
      dispatch(addToast(toast));
    }
  };

  return (
    <TeamMemberTable
      action={
        <div className="flex flex-row justify-between gap-x-3">
          <IconLinkButton
            icon={faCheck}
            linkText="Save"
            onClick={handleSubmit(onSubmit)}
          />
          <IconLinkButton
            icon={faTrashCan}
            linkText="Remove"
            onClick={() => removeMember(member.id)}
          />
        </div>
      }
      email={<span>{member.email}</span>}
      fullName={<span>{member.fullName}</span>}
      role={
        <select
          className="py-px pr-14 pl-0 w-3/4 outline-hidden focus:outline-none active:outline-hidden focus:outline-hidden bg-transparent border-none"
          {...register('role')}
          defaultValue={member.role}
        >
          {(Object.keys(TeamRole) as Array<keyof typeof TeamRole>).map(
            (key) => {
              return (
                <option key={`${key}`} value={key}>
                  {FriendlyUserRole[key]}
                </option>
              );
            }
          )}
        </select>
      }
    />
  );
};
